import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Select } from "antd";
import { NotificationManager } from "react-notifications";
import PurchaseOrderServices from "../../services/purchaseOrder.service";
import "../../assets/css/po2-design.css";

const { Option } = Select;

// Task 1908 (Feature 1863 / F5, FR-702/703/704): the backend (JCDeliverySetting CRUD,
// DeliverySettingController.cs) has existed since F5 - no screen was ever built to use it
// until Epic 1857's UI-redesign Phase 7. Matches the prototype's vSettings()/oSettings():
// a vendor manages their own VENDOR-scope recipients; an organizer manages ORGANIZATION-
// scope recipients (backend forces OwnerId to the caller's own dealerId for VENDOR scope
// server-side - same "dealer record + org flag" D9 pattern used elsewhere for organizers).
const DeliverySettings = () => {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === "ar";
  const loginDetials = useSelector((state) => state.loginReducer);
  const dealerId = loginDetials?.loginDetials?.responseData?.dealerId;
  const memberType = loginDetials?.loginDetials?.responseData?.dealerMemberTypeName;
  const isOrganizer = memberType === "Organizer";
  const scope = isOrganizer ? "ORGANIZATION" : "VENDOR";

  const [settings, setSettings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [newAppliesTo, setNewAppliesTo] = useState("SHOW_TAGGED");
  const [saving, setSaving] = useState(false);

  const [shows, setShows] = useState([]);

  const load = () => {
    if (!dealerId) return;
    setLoading(true);
    PurchaseOrderServices.GetDeliverySettings(scope, dealerId)
      .then((res) => {
        setLoading(false);
        if (res?.data?.status) setSettings(res.data.responseData || []);
      })
      .catch(() => setLoading(false));
  };

  useEffect(load, [dealerId, scope]);

  useEffect(() => {
    if (!isOrganizer) return;
    PurchaseOrderServices.GetMyShows().then((res) => {
      if (res?.data?.status) setShows(res.data.responseData || []);
    });
  }, [isOrganizer]);

  const handleAdd = () => {
    if (!newEmail.trim()) {
      NotificationManager.error(t("An email is required."));
      return;
    }
    setSaving(true);
    PurchaseOrderServices.UpsertDeliverySetting({
      scope,
      ownerId: dealerId,
      email: newEmail.trim(),
      appliesTo: newAppliesTo,
    })
      .then((res) => {
        setSaving(false);
        if (res?.data?.status) {
          NotificationManager.success(t("Recipient added."));
          setNewEmail("");
          load();
        } else {
          NotificationManager.error(t("Could not add the recipient."));
        }
      })
      .catch(() => {
        setSaving(false);
        NotificationManager.error(t("Could not add the recipient."));
      });
  };

  const handleRemove = (deliverySettingId) => {
    PurchaseOrderServices.DeactivateDeliverySetting(deliverySettingId).then((res) => {
      if (res?.data?.status) load();
    });
  };

  const appliesToLabel = (v) => (v === "ALL" ? t("All orders") : t("Show-tagged orders only"));

  return (
    <div className="po2-scope" dir={isRtl ? "rtl" : "ltr"}>
      <div className="po2-phead">
        <div>
          <h1>{t("Delivery Settings")}</h1>
          <div className="po2-desc">{t("Additional document recipients")}</div>
        </div>
      </div>

      <div className="po2-insight">
        <b>{t("The ordering retailer is always copied")}</b>
        {t("No configuration required. The addresses below are additional recipients on top of that.")}
      </div>

      <div className="po2-panel po2-flush">
        <div className="po2-ph">
          <h3>{isOrganizer ? t("Organization recipients") : t("Vendor-level recipients")}</h3>
        </div>
        <div className="po2-tblscroll" style={{ maxHeight: "none", border: "none" }}>
          <table className="po2-dt">
            <thead>
              <tr>
                <th>{t("Email")}</th>
                <th>{t("Applies to")}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {settings.map((s) => (
                <tr key={s.deliverySettingId}>
                  <td>{s.email}</td>
                  <td>
                    <span className="po2-badge">{appliesToLabel(s.appliesTo)}</span>
                  </td>
                  <td className="po2-r">
                    <button className="po2-btn po2-linky" style={{ color: "var(--po2-bad)" }} onClick={() => handleRemove(s.deliverySettingId)}>
                      {t("Remove")}
                    </button>
                  </td>
                </tr>
              ))}
              {!loading && settings.length === 0 && (
                <tr>
                  <td colSpan={3}>
                    <div className="po2-empty">
                      <p>{t("No additional recipients yet.")}</p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="po2-filters" style={{ border: "none", borderTop: "1px solid var(--po2-line)", borderRadius: 0, margin: 0 }}>
          <div className="po2-field">
            <label>{t("Email")}</label>
            <input className="po2-in" style={{ width: 240 }} placeholder="name@company.com" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} />
          </div>
          <div className="po2-field">
            <label>{t("Applies to")}</label>
            <Select style={{ width: 220 }} value={newAppliesTo} onChange={setNewAppliesTo}>
              <Option value="SHOW_TAGGED">{t("Show-tagged orders only")}</Option>
              <Option value="ALL">{t("All orders")}</Option>
            </Select>
          </div>
          <button className="po2-btn" onClick={handleAdd} disabled={saving}>
            {t("Add recipient")}
          </button>
        </div>
      </div>

      {isOrganizer && (
        <div className="po2-panel po2-flush">
          <div className="po2-ph">
            <h3>{t("Shows")}</h3>
          </div>
          <div className="po2-tblscroll" style={{ maxHeight: "none", border: "none" }}>
            <table className="po2-dt">
              <thead>
                <tr>
                  <th>{t("Show")}</th>
                  <th>{t("Location")}</th>
                </tr>
              </thead>
              <tbody>
                {shows.map((s) => (
                  <tr key={s.showId}>
                    <td>
                      <b>{s.showName}</b>
                    </td>
                    <td>{[s.city, s.state].filter(Boolean).join(", ") || "-"}</td>
                  </tr>
                ))}
                {shows.length === 0 && (
                  <tr>
                    <td colSpan={2}>
                      <div className="po2-empty">
                        <p>{t("No shows assigned to you yet.")}</p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default DeliverySettings;
